import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { ConversationMark, ConversationWordmark } from "./OlliLogo";
import { NavIcon } from "./NavIcons";
import { CommandPalette } from "./CommandPalette";
import { isIn, nav } from "../state/nav";
import OlliAssistant from "./assistant/OlliAssistant";
import { NotificationsBell } from "./NotificationsBell";
import { FacilitySwitcher } from "./FacilitySwitcher";
import { ConfirmDialog } from "./crm/ui";
import { PermissionNotice } from "./PermissionNotice";
import { openOlli } from "../lib/olli";
import { useAskOlli } from "../../hooks/useFeatureFlags";

/**
 * The new design's frame: sidebar, top bar, page search and the Olli assistant
 * (design: components/AppShell.tsx).
 *
 * Differences from the design file, none of them visible:
 *  - the menu points at this app's routes (state/nav.js); a page the design has
 *    but the app doesn't yet is shown and goes nowhere;
 *  - the account and the location list come from the real API, not sample data;
 *  - pages arrive as `children` (the app wraps each route in <Dashboard>)
 *    instead of a router <Outlet>.
 */

const stagger = (i, step = 28, base = 80) => ({ animationDelay: `${base + i * step}ms` });

/* ---------- Expanded menu ---------- */

function Group({ g, index, intro, open, onToggle, pathname }) {
  const containsActive = !!g.items?.some((i) => isIn(pathname, i.path));
  const enter = intro ? "animate-nav-in" : "";

  if (!g.items) {
    const isActive = isIn(pathname, g.path);
    const className = isActive
      ? `${enter} group flex h-11 items-center gap-3 rounded-full bg-sun px-4 text-small font-semibold text-ink shadow-glow-sun`
      : `${enter} group flex h-11 items-center gap-3 rounded-full px-4 text-small font-medium text-paper/70 hover:bg-paper/[0.07] hover:text-paper`;
    const body = (
      <>
        <NavIcon name={g.icon} tone={isActive ? "onSun" : "idle"} />
        {g.label}
      </>
    );
    // No page yet (e.g. Ask Olli): shown as designed, goes nowhere.
    return g.path ? (
      <Link to={g.path} aria-current={isActive ? "page" : undefined} style={intro ? stagger(index) : undefined} className={className}>{body}</Link>
    ) : (
      <button type="button" style={intro ? stagger(index) : undefined} className={`${className} text-left`}>{body}</button>
    );
  }

  return (
    <div className={`${enter} flex flex-col`} style={intro ? stagger(index) : undefined}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className={
          open || containsActive
            ? "group flex h-11 items-center gap-3 rounded-full px-4 text-left text-small font-semibold text-paper hover:bg-paper/[0.07]"
            : "group flex h-11 items-center gap-3 rounded-full px-4 text-left text-small font-medium text-paper/70 hover:bg-paper/[0.07] hover:text-paper"
        }
      >
        <NavIcon name={g.icon} tone={containsActive ? "active" : "idle"} />
        <span className="flex-1">{g.label}</span>
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          className={open ? "rotate-180 text-paper/60 transition-transform duration-200 ease-out" : "text-paper/40 transition-transform duration-150 ease-out"}
        />
      </button>

      <div className={open ? "grid grid-rows-[1fr] transition-[grid-template-rows] duration-250 ease-out" : "grid grid-rows-[0fr] transition-[grid-template-rows] duration-150 ease-out"}>
        <div className="overflow-hidden">
          {/* Re-keyed on open so the rows rise in each time the group opens */}
          <ul key={open ? "open" : "closed"} className="relative mb-2 ml-[26px] mt-1 flex flex-col gap-1 border-l border-paper/10 pl-3">
            {g.items.map((it, i) => {
              const isActive = isIn(pathname, it.path);
              const className = `${open ? "animate-nav-in" : ""} relative flex h-9 items-center gap-2 rounded-full px-3 text-small ${
                isActive ? "bg-paper/[0.1] font-semibold text-paper" : "text-paper/60 hover:bg-paper/[0.06] hover:text-paper"
              }`;
              const body = (
                <>
                  {isActive && <span aria-hidden className="absolute -left-[17px] top-1/2 -mt-[4px] h-2 w-2 rounded-full bg-sun shadow-glow-sun" />}
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.soon && <span className="rounded-full bg-paper/10 px-2 py-0.5 text-caption font-medium text-paper/60">Soon</span>}
                </>
              );
              return (
                <li key={`${g.id}-${it.label}`} className={it.divider ? "border-t border-paper/10 pt-1" : ""}>
                  {it.path ? (
                    <Link to={it.path} tabIndex={open ? 0 : -1} aria-current={isActive ? "page" : undefined} style={open ? stagger(i, 22, 40) : undefined} className={className}>{body}</Link>
                  ) : (
                    <button type="button" tabIndex={open ? 0 : -1} style={open ? stagger(i, 22, 40) : undefined} className={`${className} w-full text-left`}>{body}</button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------- Collapsed menu: icons, with a flyout for names and sub-pages ---------- */

function Flyout({ fly, menu, onEnter, onLeave, onPick, pathname }) {
  const g = menu.find((n) => n.id === fly.id);
  const label = fly.id === "search" ? "Search pages" : fly.id === "expand" ? "Expand menu" : g?.label;
  const items = g?.items;
  // Keep a tall flyout on screen
  const height = items ? 56 + items.length * 40 : 44;
  const top = Math.floor(Math.max(12, Math.min(fly.top, window.innerHeight - height - 12)));

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ top }}
      className={`olli fixed left-[100px] z-[60] animate-nav-in ${items ? "" : "pointer-events-none"}`}
    >
      {items ? (
        <div className="flex w-60 flex-col gap-0.5 rounded-lg bg-ink p-2 text-paper shadow-rail ring-1 ring-paper/10">
          <p className="px-3 pb-1.5 pt-2 text-caption font-medium text-paper/50">{label}</p>
          {items.map((it) => {
            const isActive = isIn(pathname, it.path);
            const className = `flex h-9 items-center gap-2 rounded-full px-3 text-small ${isActive ? "bg-sun font-semibold text-ink" : "text-paper/70 hover:bg-paper/[0.08] hover:text-paper"}`;
            const body = (
              <>
                <span className="flex-1 truncate">{it.label}</span>
                {it.soon && <span className="rounded-full bg-paper/10 px-2 py-0.5 text-caption font-medium text-paper/60">Soon</span>}
              </>
            );
            return (
              <div key={`${g.id}-${it.label}`} className={it.divider ? "mt-1 border-t border-paper/10 pt-1" : ""}>
                {it.path ? (
                  <Link to={it.path} onClick={onPick} aria-current={isActive ? "page" : undefined} className={className}>{body}</Link>
                ) : (
                  <button type="button" className={`${className} w-full text-left`}>{body}</button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex h-11 items-center gap-2 rounded-full bg-ink px-4 text-small font-medium text-paper shadow-rail ring-1 ring-paper/10">
          {label}
          {fly.id === "search" && <span className="text-paper/50">⌘K</span>}
        </div>
      )}
    </div>
  );
}

function RailItem({ g, pathname, onShow, onHide }) {
  const active = g.items ? g.items.some((i) => isIn(pathname, i.path)) : isIn(pathname, g.path);
  const show = (e) => onShow(g.id, e.currentTarget);
  const cls = active && !g.items ? "bg-sun text-ink shadow-glow-sun" : active ? "bg-paper/[0.1] text-paper" : "text-paper/70 hover:bg-paper/[0.07] hover:text-paper";
  const base = `group mx-auto flex h-11 w-12 items-center justify-center rounded-full ${cls}`;
  const tone = active && !g.items ? "onSun" : active ? "active" : "idle";

  if (!g.items && g.path) {
    return (
      <Link to={g.path} aria-label={g.label} aria-current={active ? "page" : undefined} onMouseEnter={show} onMouseLeave={onHide} onFocus={show} onBlur={onHide} className={base}>
        <NavIcon name={g.icon} tone={tone} />
      </Link>
    );
  }
  return (
    <button aria-label={g.label} aria-haspopup={g.items ? "menu" : undefined} onMouseEnter={show} onMouseLeave={onHide} onFocus={show} onClick={show} className={base}>
      <NavIcon name={g.icon} tone={tone} />
    </button>
  );
}

/* ---------- Sidebar ---------- */

function Sidebar({ menu, account, collapsed, onCollapse, onSearch, onSignOut }) {
  const { pathname } = useLocation();
  const activeGroup = menu.find((g) => g.items?.some((i) => isIn(pathname, i.path)))?.id;
  const [open, setOpen] = useState(() => new Set(activeGroup ? [activeGroup] : []));
  const [fly, setFly] = useState(null);
  const hideTimer = useRef(undefined);

  // The menu rises in once per session, not on every page change.
  const introRef = useRef(true);
  const intro = introRef.current;
  useEffect(() => {
    const t = setTimeout(() => (introRef.current = false), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (activeGroup) setOpen((s) => (s.has(activeGroup) ? s : new Set(s).add(activeGroup)));
  }, [activeGroup]);

  useEffect(() => setFly(null), [collapsed, pathname]);

  const toggle = (id) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const showFly = (id, el) => {
    clearTimeout(hideTimer.current);
    setFly({ id, top: el.getBoundingClientRect().top });
  };
  const hideFly = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setFly(null), 140);
  };
  const keepFly = () => clearTimeout(hideTimer.current);

  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const signOut = () => setConfirmSignOut(true);
  const doSignOut = () => {
    setConfirmSignOut(false);
    onSignOut();
  };

  return (
    <>
    {confirmSignOut && <ConfirmDialog tone="neutral" title="Log out of Olli?" body={<>You'll need to sign in again to get back to {account.clinic}. Drafts you haven't saved will be lost.</>} confirmLabel="Log out" onConfirm={doSignOut} onCancel={() => setConfirmSignOut(false)} />}
    <aside className={`sticky top-0 h-screen shrink-0 p-3 transition-[width] duration-250 ease-out ${collapsed ? "w-24" : "w-[300px]"}`}>
      {/* ds-ok: rail gradient and glows are tokens in olli.css */}
      <div className="flex h-full flex-col overflow-hidden whitespace-nowrap rounded-xl bg-[image:var(--gradient-rail)] text-paper shadow-rail ring-1 ring-paper/10">
        {collapsed ? (
          <div className="flex shrink-0 flex-col items-center gap-2 pb-2 pt-5">
            <Link to="/dashboard" aria-label="Olli AI, dashboard">
              <ConversationMark tone="black" className="h-9 w-9" />
            </Link>
            <button
              onClick={() => onCollapse(false)}
              aria-label="Expand menu"
              onMouseEnter={(e) => showFly("expand", e.currentTarget)}
              onMouseLeave={hideFly}
              className="flex h-9 w-9 items-center justify-center rounded-full text-paper/50 hover:bg-paper/10 hover:text-paper"
            >
              <PanelLeftOpen size={18} strokeWidth={1.75} />
            </button>
          </div>
        ) : (
          <div className="flex h-20 shrink-0 items-center justify-between pl-6 pr-4">
            <Link to="/dashboard" aria-label="Olli AI, dashboard" className="animate-fade-in">
              <ConversationWordmark tone="black" className="h-8 w-auto" />
            </Link>
            <button
              onClick={() => onCollapse(true)}
              aria-label="Collapse menu"
              title="Collapse menu"
              className="flex h-9 w-9 items-center justify-center rounded-full text-paper/50 hover:bg-paper/10 hover:text-paper"
            >
              <PanelLeftClose size={18} strokeWidth={1.75} />
            </button>
          </div>
        )}

        <div className="shrink-0 px-3 pb-3">
          {collapsed ? (
            <button
              onClick={onSearch}
              aria-label="Search pages"
              onMouseEnter={(e) => showFly("search", e.currentTarget)}
              onMouseLeave={hideFly}
              className="mx-auto flex h-11 w-12 items-center justify-center rounded-full bg-paper/[0.06] text-paper/70 ring-1 ring-paper/10 hover:bg-paper/10 hover:text-paper"
            >
              <Search size={18} strokeWidth={1.75} />
            </button>
          ) : (
            <button
              onClick={onSearch}
              className="flex h-11 w-full items-center gap-3 rounded-full bg-paper/[0.06] px-4 text-small text-paper/50 ring-1 ring-paper/10 hover:bg-paper/10 hover:text-paper/80"
            >
              <Search size={18} strokeWidth={1.75} />
              <span className="flex-1 text-left">Search pages</span>
              <kbd className="rounded-xs bg-paper/10 px-1.5 py-0.5 font-sans text-caption font-medium text-paper/60">⌘K</kbd>
            </button>
          )}
        </div>

        <nav
          aria-label="Main"
          className="flex-1 overflow-y-auto px-3 pb-6 pt-1 [mask-image:linear-gradient(to_bottom,transparent,black_12px,black_calc(100%-32px),transparent)] [scrollbar-width:none]"
        >
          <div className="flex flex-col gap-1.5">
            {collapsed
              ? menu.map((g) => <RailItem key={g.id} g={g} pathname={pathname} onShow={showFly} onHide={hideFly} />)
              : menu.map((g, i) => <Group key={g.id} g={g} index={i} intro={intro} open={open.has(g.id)} onToggle={() => toggle(g.id)} pathname={pathname} />)}
          </div>
        </nav>

        <div className="shrink-0 p-3">
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <span className="rounded-full bg-[image:var(--gradient-sun)] p-[2px]" title={`${account.name}, ${account.clinic}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-caption font-semibold text-paper">{account.initials}</span>
              </span>
              <button onClick={signOut} aria-label="Log out" title="Log out" className="flex h-9 w-9 items-center justify-center rounded-full text-paper/60 hover:bg-paper/10 hover:text-paper">
                <LogOut size={18} strokeWidth={1.75} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg bg-paper/[0.06] p-2.5 ring-1 ring-paper/10">
              <span className="rounded-full bg-[image:var(--gradient-sun)] p-[2px]">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-caption font-semibold text-paper">{account.initials}</span>
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-small font-semibold">{account.name}</span>
                <span className="truncate text-caption text-paper/60">{account.clinic}</span>
              </div>
              <button onClick={signOut} aria-label="Log out" title="Log out" className="flex h-9 w-9 items-center justify-center rounded-full text-paper/60 hover:bg-paper/10 hover:text-paper">
                <LogOut size={18} strokeWidth={1.75} />
              </button>
            </div>
          )}
        </div>
      </div>

      {collapsed && fly && createPortal(<Flyout fly={fly} menu={menu} onEnter={keepFly} onLeave={hideFly} onPick={() => setFly(null)} pathname={pathname} />, document.body)}
    </aside>
    </>
  );
}

/** Account shown in the sidebar: name and initials as in the design (a leading "Dr." is skipped). */
export function accountFrom(name, clinic) {
  const display = (name || "").trim() || "Your account";
  const initials = display
    .replace(/^dr\.?\s*/i, "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return { name: display, clinic: clinic || "Your clinic", initials };
}

/**
 * The frame. `account` = { name, clinic, initials }; `hide` = menu group ids to
 * leave out (e.g. a module the clinic's plan doesn't include); `onSignOut`
 * runs after the user confirms "Log out".
 */
/** True while the viewport matches the media query (kept live on resize). */
function useMedia(query) {
  const get = () => typeof window !== "undefined" && window.matchMedia(query).matches;
  const [match, setMatch] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

/**
 * Narrow screens (as in the admin panel): below 1280px the menu starts as the
 * icon rail; below 640px there is no rail — a sticky top bar carries the logo,
 * search, notifications, the Olli button (the floating one would sit on reply
 * boxes there) and the menu button, and the menu opens over the page, closing
 * on a page change, Escape or a tap outside. The location switcher sits under
 * the top bar.
 */
export function AppShell({ account, hide = [], onSignOut, children }) {
  const { pathname } = useLocation();
  const narrow = useMedia("(max-width: 1279px)"); // below xl: the menu starts as a rail
  const phone = useMedia("(max-width: 639px)"); // below sm: no rail, the menu opens over the page
  const [collapsed, setCollapsed] = useState(narrow);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const askOlliOn = useAskOlli();
  const menu = nav.filter((g) => !hide.includes(g.id) && (askOlliOn || g.id !== "ask"));

  useEffect(() => setCollapsed(narrow), [narrow]);
  useEffect(() => setMenuOpen(false), [pathname, phone]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearch((s) => !s);
      }
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openSearch = () => {
    setMenuOpen(false);
    setSearch(true);
  };

  return (
    <div className="olli relative flex min-h-screen overflow-clip bg-mist bg-[image:var(--gradient-morning)] bg-fixed font-sans text-ink">
      {phone ? (
        menuOpen && (
          <div className="fixed inset-0 z-[70] flex">
            <button type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-ink/40" />
            <div className="relative h-full w-[300px] max-w-[calc(100%-24px)] animate-nav-in">
              <Sidebar menu={menu} account={account} collapsed={false} onCollapse={() => setMenuOpen(false)} onSearch={openSearch} onSignOut={onSignOut} />
            </div>
          </div>
        )
      ) : (
        <Sidebar menu={menu} account={account} collapsed={collapsed} onCollapse={setCollapsed} onSearch={() => setSearch(true)} onSignOut={onSignOut} />
      )}
      <main className="min-w-0 flex-1">
        {phone ? (
          <>
            <div className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 bg-mist/80 pl-4 pr-2 backdrop-blur">
              <Link to="/dashboard" aria-label="Olli AI, dashboard" className="shrink-0">
                <ConversationWordmark tone="white" className="h-7 w-auto" />
              </Link>
              <div className="flex items-center gap-0.5">
                <button type="button" onClick={() => setSearch(true)} aria-label="Search pages" className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-paper">
                  <Search size={20} strokeWidth={1.75} />
                </button>
                <NotificationsBell />
                {askOlliOn && !pathname.startsWith("/ask-olli") && (
                  <button type="button" onClick={() => openOlli()} aria-label="Ask Olli" title="Ask Olli" className="flex h-10 w-10 items-center justify-center rounded-full bg-ink">
                    <ConversationMark tone="black" className="h-7 w-7" />
                  </button>
                )}
                <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-expanded={menuOpen} className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-paper">
                  <Menu size={20} strokeWidth={1.75} />
                </button>
              </div>
            </div>
            <div className="relative z-20 flex justify-end px-4 pb-2 pt-1">
              <FacilitySwitcher org={account.clinic.split(",")[0]} />
            </div>
          </>
        ) : (
          <div className="relative z-30 flex items-center justify-end gap-3 px-4 pb-4 pt-5 sm:px-8">
            <FacilitySwitcher org={account.clinic.split(",")[0]} />
            <NotificationsBell />
          </div>
        )}
        {children}
      </main>
      <CommandPalette open={search} onClose={() => setSearch(false)} />
      {askOlliOn && <OlliAssistant />}
      <PermissionNotice />
    </div>
  );
}
